import type * as React from 'react';

import type { GLPSecondaryNavItem } from './glp-secondary-sidebar';
import type { NavItem } from './glp-sidebar';

/**
 * How a nav link decides it is the current page.
 * - `exact`: the pathname equals `path` (or one of `aliases`).
 * - `prefix`: the pathname is `path` or below it (`/sales` matches `/sales/orders`).
 *   `/` only ever matches itself.
 */
export type NavMatch = 'exact' | 'prefix';

export interface NavTreeLink {
  /** Stable id; defaults to `path`. */
  id?: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Defaults to `exact`. */
  match?: NavMatch;
  /** Other paths that count as this link (matched with the same rule). */
  aliases?: string[];
  external?: boolean;
}

/** A top-level module with its section (secondary sidebar) links. */
export interface NavTreeModule extends NavTreeLink {
  sections?: NavTreeLink[];
}

const matchesOne = (pathname: string, path: string, match: NavMatch) => {
  if (match === 'exact' || path === '/') return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
};

/** Whether `link` is the current page (or, with `prefix`, an ancestor of it). */
export function isNavPathActive(pathname: string, link: NavTreeLink): boolean {
  const match = link.match ?? 'exact';
  return [link.path, ...(link.aliases ?? [])].some((p) => matchesOne(pathname, p, match));
}

/** The module the pathname belongs to, if any. */
export function findActiveNavModule<M extends NavTreeModule>(
  modules: M[],
  pathname: string,
): M | undefined {
  return modules.find((m) => isNavPathActive(pathname, m));
}

/** The section link of `module` that is the current page, if any. */
export function findActiveNavSection(
  module: NavTreeModule | undefined,
  pathname: string,
): NavTreeLink | undefined {
  return module?.sections?.find((s) => isNavPathActive(pathname, s));
}

/**
 * Turns a nav tree into `GLPSidebar` items for the current pathname: `active`
 * flags, `href` (keeps middle-click / open-in-new-tab working), `onClick`
 * routed through the host's navigate, and each module's sections as
 * `children` (used by the mobile drawer's drill-down).
 */
export function buildNavItems(
  modules: NavTreeModule[],
  pathname: string,
  navigate: (path: string) => void,
): NavItem[] {
  const toItem = (link: NavTreeLink): NavItem => ({
    label: link.label,
    icon: link.icon,
    href: link.path,
    active: isNavPathActive(pathname, link),
    onClick: link.external ? undefined : () => navigate(link.path),
  });
  return modules.map((module) => ({
    ...toItem(module),
    children: module.sections?.map(toItem),
  }));
}

/** A module's section links as `GLPSecondarySidebar` items for the current pathname. */
export function buildSecondaryNavItems(
  module: NavTreeModule | undefined,
  pathname: string,
): GLPSecondaryNavItem[] {
  return (module?.sections ?? []).map((link) => ({
    path: link.path,
    label: link.label,
    icon: link.icon,
    external: link.external,
    active: isNavPathActive(pathname, link),
  }));
}
